import React, { useEffect, useMemo, useRef } from "react";

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};

const sanitizeMessageText = (value) => {
  if (typeof value !== "string") return "";
  return value
    .split("")
    .filter((char) => {
      const code = char.charCodeAt(0);
      return code === 9 || code === 10 || code === 13 || (code >= 32 && code !== 127);
    })
    .join("");
};

const toTimeValue = (value) => {
  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) return 0;
  return timestamp;
};

export const MessageList = ({
  messages,
  loading,
  loadingOlder,
  hasMore,
  onLoadOlder,
}) => {
  const listRef = useRef(null);
  const prevLengthRef = useRef(0);

  const sortedMessages = useMemo(
    () =>
      [...messages].sort((a, b) => {
        const timeDiff = toTimeValue(a.createdAt) - toTimeValue(b.createdAt);
        if (timeDiff !== 0) return timeDiff;
        const aId = String(a.id || a.clientTempId || "");
        const bId = String(b.id || b.clientTempId || "");
        return aId.localeCompare(bId);
      }),
    [messages]
  );

  useEffect(() => {
    const root = listRef.current;
    if (!root) return;

    const newMessagesAdded = sortedMessages.length > prevLengthRef.current;
    if (newMessagesAdded && !loadingOlder) {
      root.scrollTop = root.scrollHeight;
    }
    prevLengthRef.current = sortedMessages.length;
  }, [sortedMessages, loadingOlder]);

  if (loading) {
    return <div className="ticket-chat-loading">Loading chat history...</div>;
  }

  return (
    <div className="ticket-chat-list-wrap">
      <div className="ticket-chat-list-actions">
        <button
          className="ticket-chat-older-btn"
          type="button"
          onClick={onLoadOlder}
          disabled={!hasMore || loadingOlder}
        >
          {loadingOlder ? "Loading..." : "Load older messages"}
        </button>
      </div>

      <div className="ticket-chat-list" ref={listRef}>
        {sortedMessages.length === 0 ? (
          <div className="ticket-chat-empty">No messages yet</div>
        ) : (
          sortedMessages.map((message, index) => (
            <article
              key={message.id || message.clientTempId || `${message.createdAt}-${index}`}
              className={`ticket-chat-row ${message.isOwn ? "own" : "other"}`}
            >
              <div className={`ticket-chat-item ${message.pending ? "pending" : ""}`}>
                {!message.isOwn ? (
                  <div className="ticket-chat-sender">{message.senderName || "Unknown"}</div>
                ) : null}
                <p>{sanitizeMessageText(message.message)}</p>
                <div className="ticket-chat-meta">
                  <span
                    className={`ticket-chat-direction ${message.isOwn ? "outbound" : "inbound"}`}
                  >
                    {message.isOwn ? "Outbound" : "Inbound"}
                  </span>
                  <time>{formatDate(message.createdAt)}</time>
                  {message.pending ? <span>Sending...</span> : null}
                </div>
              </div>
            </article>
          ))
        )}
      </div>
    </div>
  );
};

export default MessageList;
